import React, { useState, useEffect, useRef, Fragment } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  IconButton,
  Select,
  MenuItem,
  Slider,
  alpha,
  Tooltip,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from "@mui/material";
import { useSelector } from "react-redux";
import { IApplicationState } from "@/store-redux";
import SchoolIcon from "@mui/icons-material/School";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SpeedIcon from "@mui/icons-material/Speed";
import Swal from "sweetalert2";
import { fetchDictionary } from "@/services-react-query/api/voca.service";
import { IPronunciationAnalyzeOutput, pronunciationAnalyze } from "@/services-react-query/api/practice.service";
import PhonemeBreakdown from "@/components/PhonemeBreakdown";
import WaveformRecorder from "@/components/WaveformRecorder";
import { computeLinking, ILinkingInfo } from "./linkingHelper";
import { getWordPronunciationColor, hasVowelPronunciationError } from "@/helpers";

interface PronunciationGuideDisplayProps {
  text: string;
  onClose?: () => void;
  autoLoad?: boolean;
  onSelectTargetText?: (selectedText: string) => void;
  selectedTargetText?: string;
}

export default function PronunciationGuideDisplay({
  text,
  onClose,
  autoLoad = true,
  onSelectTargetText,
  selectedTargetText,
}: PronunciationGuideDisplayProps) {
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  const [loadingGuide, setLoadingGuide] = useState(false);
  const [sentenceGuide, setSentenceGuide] = useState<any | null>(null);
  const [ttsRate, setTtsRate] = useState<number>(0.85);

  // Available English voices & selected voice state
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("fong_selected_tts_voice") || "";
    }
    return "";
  });

  useEffect(() => {
    const loadVoices = () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        const all = window.speechSynthesis.getVoices();
        if (all.length > 0) {
          // Exclude robotic, novelty, and noisy legacy synthesizer voices
          const NOISY_VOICE_KEYWORDS = [
            "whisper",
            "zarvox",
            "trinoids",
            "cellos",
            "bells",
            "bad news",
            "bahh",
            "boing",
            "albert",
            "ralph",
            "organ",
            "deranged",
            "good news",
            "hysterical",
            "pipe organ",
            "wobble",
            "jester",
            "superstar",
            "bubbles",
            "junior",
            "kathy",
            "princess",
            "fred",
            "bruce",
            "agnes",
            "vicki",
            "compact",
          ];

          // Strictly filter only US and UK English voices with clear natural pronunciation
          const clearUsUk = all.filter((v) => {
            const lang = v.lang.toLowerCase();
            const name = v.name.toLowerCase();

            const isUs = lang === "en-us" || lang === "en_us" || (lang.includes("us") && lang.startsWith("en"));
            const isUk =
              lang === "en-gb" ||
              lang === "en_gb" ||
              lang === "en-uk" ||
              (lang.includes("gb") && lang.startsWith("en")) ||
              (lang.includes("uk") && lang.startsWith("en"));

            if (!isUs && !isUk) return false;

            // Reject novelty / robotic voices
            if (NOISY_VOICE_KEYWORDS.some((kw) => name.includes(kw))) {
              return false;
            }

            return true;
          });

          // Deduplicate multiple versions of the same voice (e.g. standard vs enhanced vs premium)
          const voiceMap = new Map<string, SpeechSynthesisVoice>();
          clearUsUk.forEach((v) => {
            const baseKey = v.name
              .toLowerCase()
              .replace(/\s*\((enhanced|premium|natural|compact|siri)\)/gi, "")
              .replace(/^google\s+/i, "google_")
              .replace(/^microsoft\s+/i, "ms_")
              .replace(/\s*-\s*english.*/i, "")
              .trim();

            const existing = voiceMap.get(baseKey);
            if (!existing) {
              voiceMap.set(baseKey, v);
            } else {
              const vQuality = v.name.includes("Premium")
                ? 4
                : v.name.includes("Enhanced")
                  ? 3
                  : v.name.includes("Natural")
                    ? 3
                    : 1;
              const exQuality = existing.name.includes("Premium")
                ? 4
                : existing.name.includes("Enhanced")
                  ? 3
                  : existing.name.includes("Natural")
                    ? 3
                    : 1;
              if (vQuality > exQuality) {
                voiceMap.set(baseKey, v);
              }
            }
          });

          const uniqueVoices = Array.from(voiceMap.values());

          // Sort: US first, then UK; Natural/Google voices on top
          uniqueVoices.sort((a, b) => {
            const isAUk = a.lang.toLowerCase().includes("gb") || a.lang.toLowerCase().includes("uk");
            const isBUk = b.lang.toLowerCase().includes("gb") || b.lang.toLowerCase().includes("uk");
            if (isAUk !== isBUk) {
              return isAUk ? 1 : -1;
            }
            const aNatural = a.name.includes("Google") || a.name.includes("Natural") || a.name.includes("Samantha");
            const bNatural = b.name.includes("Google") || b.name.includes("Natural") || b.name.includes("Samantha");
            if (aNatural && !bNatural) return -1;
            if (!aNatural && bNatural) return 1;
            return a.name.localeCompare(b.name);
          });

          const voiceList = uniqueVoices.length > 0 ? uniqueVoices : all.filter((v) => v.lang.toLowerCase().startsWith("en"));
          setAvailableVoices(voiceList);

          const saved = localStorage.getItem("fong_selected_tts_voice");
          if (saved && voiceList.some((v) => v.voiceURI === saved)) {
            setSelectedVoiceURI(saved);
          } else {
            const defaultV =
              voiceList.find((v) => v.name.toLowerCase().includes("natural") && v.lang.toLowerCase().includes("us")) ||
              voiceList.find((v) => v.name.toLowerCase().includes("google us")) ||
              voiceList.find((v) => v.name.includes("Samantha")) ||
              voiceList.find((v) => v.lang === "en-US") ||
              voiceList[0];
            if (defaultV) {
              setSelectedVoiceURI(defaultV.voiceURI);
              localStorage.setItem("fong_selected_tts_voice", defaultV.voiceURI);
            }
          }
        }
      }
    };

    loadVoices();
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  const getChosenVoice = () => {
    if (typeof window === "undefined" || !window.speechSynthesis) return undefined;
    const voices = window.speechSynthesis.getVoices();
    return (
      (selectedVoiceURI && voices.find((v) => v.voiceURI === selectedVoiceURI)) ||
      voices.find((v) => (v.lang === "en-US" || v.lang === "en-GB") && (v.name.toLowerCase().includes("google") || v.localService === false)) ||
      voices.find((v) => v.lang.startsWith("en-US") || v.lang.startsWith("en-GB")) ||
      voices.find((v) => v.lang.startsWith("en"))
    );
  };

  const getVoiceDisplayInfo = (voice: SpeechSynthesisVoice) => {
    const lang = voice.lang.toLowerCase();
    const nameLower = voice.name.toLowerCase();
    const isUk = lang.includes("gb") || lang.includes("uk") || lang === "en-gb";
    const flag = isUk ? "🇬🇧" : "🇺🇸";
    const region = isUk ? "Anh - Anh" : "Anh - Mỹ";

    const cleanName = voice.name
      .replace(/^Google\s+/i, "")
      .replace(/^Microsoft\s+/i, "")
      .replace(/\s+Online\s+\(Natural\)/i, " ✨")
      .replace(/\s*\((enhanced|premium)\)/i, " ⭐")
      .replace(/\s*-\s*English\s*\(.*?\)/i, "")
      .replace(/\s+English/i, "")
      .trim();

    let gender = "";
    if (
      nameLower.includes("female") ||
      ["samantha", "victoria", "ava", "allison", "susan", "serena", "karen", "jenny", "aria", "sara", "sonia", "libby", "maisie"].some(
        (n) => nameLower.includes(n)
      )
    ) {
      gender = "Nữ";
    } else if (
      nameLower.includes("male") ||
      ["alex", "daniel", "oliver", "tom", "aaron", "arthur", "guy", "davis", "jason", "tony", "ryan", "thomas", "alfie", "george"].some(
        (n) => nameLower.includes(n)
      )
    ) {
      gender = "Nam";
    }

    const tag = gender ? `${region} · ${gender}` : region;

    return {
      flag,
      region: tag,
      displayName: cleanName || voice.name,
      fullName: `${flag} ${cleanName || voice.name} (${tag})`,
    };
  };

  // Real-time TTS playback tracking & equalizer animation states
  const [playingSentenceIdx, setPlayingSentenceIdx] = useState<number | null>(null);
  const [playingWordGlobalIdx, setPlayingWordGlobalIdx] = useState<number | null>(null);
  const [spokenWordIndices, setSpokenWordIndices] = useState<Set<number>>(new Set());
  const playbackTimerRef = useRef<any>(null);

  // Sentence-level recording & assessment states
  const [activeSentenceRecIdx, setActiveSentenceRecIdx] = useState<number | null>(null);
  const [sentIsRecording, setSentIsRecording] = useState<boolean>(false);
  const [sentRecordingTime, setSentRecordingTime] = useState<number>(0);
  const [sentAudioUrl, setSentAudioUrl] = useState<string | null>(null);
  const [sentIsAnalyzing, setSentIsAnalyzing] = useState<boolean>(false);
  const [sentResult, setSentResult] = useState<IPronunciationAnalyzeOutput | null>(null);
  const [selectedDetailWordIdx, setSelectedDetailWordIdx] = useState<number | null>(0);

  // Simple Auto-Looping Shadowing states & handlers
  const [shadowingSentenceIdx, setShadowingSentenceIdx] = useState<number | null>(null);
  const [shadowingTargetText, setShadowingTargetText] = useState<string | null>(null);
  const [shadowingSpeed, setShadowingSpeed] = useState<number>(0.85);
  const [shadowingPauseInterval, setShadowingPauseInterval] = useState<number>(1.5); // seconds between loops
  const [shadowingLoopLimit, setShadowingLoopLimit] = useState<number>(0); // 0 = infinite, 3, 5, 10
  const [shadowingCurrentLoop, setShadowingCurrentLoop] = useState<number>(1);
  const [shadowingIsLooping, setShadowingIsLooping] = useState<boolean>(false);
  const [shadowingActiveTargetWordIdx, setShadowingActiveTargetWordIdx] = useState<number>(-1);

  const shadowingTimerRef = useRef<any>(null);
  const shadowingPlaybackTimerRef = useRef<any>(null);
  const shadowingIsActiveRef = useRef<boolean>(false);

  const stopShadowingLoop = () => {
    shadowingIsActiveRef.current = false;
    if (shadowingTimerRef.current) {
      clearTimeout(shadowingTimerRef.current);
      shadowingTimerRef.current = null;
    }
    if (shadowingPlaybackTimerRef.current) {
      clearInterval(shadowingPlaybackTimerRef.current);
      shadowingPlaybackTimerRef.current = null;
    }
    window.speechSynthesis?.cancel();
    setShadowingIsLooping(false);
    setShadowingActiveTargetWordIdx(-1);
  };

  const playOneShadowingLoop = (
    textToPlay: string,
    speed: number,
    pauseSec: number,
    maxLoops: number,
    loopNum: number
  ) => {
    if (!shadowingIsActiveRef.current) return;

    if (shadowingPlaybackTimerRef.current) {
      clearInterval(shadowingPlaybackTimerRef.current);
      shadowingPlaybackTimerRef.current = null;
    }

    window.speechSynthesis?.cancel();
    const utterance = new SpeechSynthesisUtterance(textToPlay);
    utterance.lang = "en-US";
    utterance.rate = speed;

    const chosenVoice = getChosenVoice();
    if (chosenVoice) {
      utterance.voice = chosenVoice;
      utterance.lang = chosenVoice.lang;
    }

    // Calculate exact character boundary ranges for each word token
    const targetTokens = textToPlay.trim().split(/\s+/);
    const wordRanges: { start: number; end: number }[] = [];
    let cursor = 0;
    for (let i = 0; i < targetTokens.length; i++) {
      const tok = targetTokens[i];
      const foundStart = textToPlay.indexOf(tok, cursor);
      const start = foundStart !== -1 ? foundStart : cursor;
      const end = start + tok.length;
      wordRanges.push({ start, end });
      cursor = end;
    }

    // Hybrid timeline calculation for 100% reliable word highlight fallbacks
    let cumulativeMs = 0;
    const wordTimeline = targetTokens.map((tok, idx) => {
      const cleanTok = tok.replace(/[.,;:!?]$/, "");
      const sylCount = Math.max(1, Math.ceil(cleanTok.length / 3.2));
      const durationMs = Math.max(180, (sylCount * 210) / speed);
      const startMs = cumulativeMs;
      cumulativeMs += durationMs;
      return { idx, startMs, endMs: cumulativeMs };
    });

    // Track whether browser's onboundary event fired at least once
    // If it fires → it's accurate, stop the timer fallback to avoid flicker
    let boundaryFired = false;

    // Set first word active immediately when speech begins
    if (wordRanges.length > 0) {
      setShadowingActiveTargetWordIdx(0);
    }

    // Real-time timeline ticker fallback (only used if onboundary never fires)
    const startTimeMs = Date.now();
    shadowingPlaybackTimerRef.current = setInterval(() => {
      if (!shadowingIsActiveRef.current) return;
      // If browser already gave us accurate boundary events, stop the timer
      if (boundaryFired) {
        clearInterval(shadowingPlaybackTimerRef.current);
        shadowingPlaybackTimerRef.current = null;
        return;
      }
      const elapsed = Date.now() - startTimeMs;
      const activeItem = wordTimeline.find((w) => elapsed >= w.startMs && elapsed < w.endMs);
      if (activeItem) {
        setShadowingActiveTargetWordIdx(activeItem.idx);
      }
    }, 50);

    utterance.onboundary = (event: SpeechSynthesisEvent) => {
      if (event.name !== "word") return;
      boundaryFired = true;
      // Stop the timer fallback now that we have accurate boundary events
      if (shadowingPlaybackTimerRef.current) {
        clearInterval(shadowingPlaybackTimerRef.current);
        shadowingPlaybackTimerRef.current = null;
      }
      const charIdx = event.charIndex;
      for (let i = 0; i < wordRanges.length; i++) {
        const range = wordRanges[i];
        if (charIdx >= range.start - 2 && (i === wordRanges.length - 1 || charIdx < wordRanges[i + 1].start)) {
          setShadowingActiveTargetWordIdx(i);
          break;
        }
      }
    };

    utterance.onend = () => {
      if (shadowingPlaybackTimerRef.current) {
        clearInterval(shadowingPlaybackTimerRef.current);
        shadowingPlaybackTimerRef.current = null;
      }

      // Keep last word highlighted when utterance ends
      if (wordRanges.length > 0) {
        setShadowingActiveTargetWordIdx(wordRanges.length - 1);
      }

      if (!shadowingIsActiveRef.current) return;

      if (maxLoops > 0 && loopNum >= maxLoops) {
        setTimeout(() => {
          stopShadowingLoop();
        }, 500);
        return;
      }

      const nextLoop = loopNum + 1;
      setShadowingCurrentLoop(nextLoop);
      shadowingTimerRef.current = setTimeout(() => {
        if (shadowingIsActiveRef.current) {
          setShadowingActiveTargetWordIdx(0);
          playOneShadowingLoop(textToPlay, speed, pauseSec, maxLoops, nextLoop);
        }
      }, pauseSec * 1000);
    };

    utterance.onerror = () => {
      if (shadowingPlaybackTimerRef.current) {
        clearInterval(shadowingPlaybackTimerRef.current);
        shadowingPlaybackTimerRef.current = null;
      }
      stopShadowingLoop();
    };

    setShadowingIsLooping(true);
    window.speechSynthesis?.speak(utterance);
  };

  const startShadowingLoop = (targetText?: string, speed?: number, pauseSec?: number, maxLoops?: number) => {
    const textToPlay = targetText || shadowingTargetText || "";
    if (!textToPlay) return;

    stopShadowingLoop();
    shadowingIsActiveRef.current = true;
    const currentSpeed = speed !== undefined ? speed : shadowingSpeed;
    const currentPause = pauseSec !== undefined ? pauseSec : shadowingPauseInterval;
    const currentLimit = maxLoops !== undefined ? maxLoops : shadowingLoopLimit;

    setShadowingCurrentLoop(1);
    playOneShadowingLoop(textToPlay, currentSpeed, currentPause, currentLimit, 1);
  };

  const handleOpenShadowingStudio = (sentWords: any[], sIdx: number, customTargetText?: string) => {
    stopShadowingLoop();

    const defaultText = sentWords
      .map(({ gw }) => (gw.originalToken || gw.word || "").trim())
      .join(" ")
      .trim();
    const textToPlay = customTargetText || defaultText;

    setShadowingSentenceIdx(sIdx);
    setShadowingTargetText(textToPlay);
  };

  const sentMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const sentStreamRef = useRef<MediaStream | null>(null);
  const sentTimerRef = useRef<any>(null);

  const startSentenceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      sentStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      sentMediaRecorderRef.current = mediaRecorder;
      const chunks: Blob[] = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setSentAudioUrl(url);
      };

      mediaRecorder.start();
      setSentIsRecording(true);
      setSentRecordingTime(0);
      setSentAudioUrl(null);
      setSentResult(null);

      if (sentTimerRef.current) clearInterval(sentTimerRef.current);
      sentTimerRef.current = setInterval(() => {
        setSentRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Failed to start sentence recording:", err);
      Swal.fire("Lỗi", "Không thể truy cập microphone để ghi âm câu này.", "error");
    }
  };

  const stopSentenceRecording = () => {
    if (sentTimerRef.current) {
      clearInterval(sentTimerRef.current);
      sentTimerRef.current = null;
    }
    if (sentMediaRecorderRef.current && sentMediaRecorderRef.current.state !== "inactive") {
      sentMediaRecorderRef.current.stop();
    }
    if (sentStreamRef.current) {
      sentStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    setSentIsRecording(false);
  };

  const handleAnalyzeSentence = async (sentenceText: string) => {
    if (!sentAudioUrl || !sentenceText) return;
    setSentIsAnalyzing(true);
    try {
      const blob = await (await fetch(sentAudioUrl)).blob();
      const file = new File([blob], `sentence-practice.webm`, { type: "audio/webm" });

      const res = await pronunciationAnalyze({
        sentence: sentenceText,
        audioFile: file,
      });

      if (res) {
        setSentResult(res);
        if (res.words && res.words.length > 0) {
          const firstErr = res.words.findIndex((w: any) => (w.score || 0) < 80);
          setSelectedDetailWordIdx(firstErr !== -1 ? firstErr : 0);
        }
      }
    } catch (error: any) {
      console.error("Sentence analysis error:", error);
      if (error?.response?.status === 402) return;
      const msg = error?.response?.data?.message || error?.message || "Đánh giá phát âm cho câu này thất bại.";
      Swal.fire("Lỗi", msg, "error");
    } finally {
      setSentIsAnalyzing(false);
    }
  };

  // Single-word practice states & handlers
  const [practiceWordObj, setPracticeWordObj] = useState<any | null>(null);
  const [practiceWordIsRecording, setPracticeWordIsRecording] = useState<boolean>(false);
  const [practiceWordRecTime, setPracticeWordRecTime] = useState<number>(0);
  const [practiceWordAudioUrl, setPracticeWordAudioUrl] = useState<string | null>(null);
  const [practiceWordIsAnalyzing, setPracticeWordIsAnalyzing] = useState<boolean>(false);
  const [practiceWordScore, setPracticeWordScore] = useState<number | null>(null);
  const [practiceWordPhonemes, setPracticeWordPhonemes] = useState<any[] | null>(null);

  const practiceWordRecorderRef = useRef<MediaRecorder | null>(null);
  const practiceWordStreamRef = useRef<MediaStream | null>(null);
  const practiceWordTimerRef = useRef<any>(null);

  const handleOpenPracticeWord = (wordObj: any) => {
    setPracticeWordObj(wordObj);
    setPracticeWordAudioUrl(null);
    setPracticeWordScore(null);
    setPracticeWordPhonemes(null);
  };

  const handlePlaySingleWord = (wordText: string) => {
    if (!wordText || typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const clean = wordText.replace(/[.,;:!?]$/, "").trim();
    if (!clean) return;

    const utterance = new SpeechSynthesisUtterance(clean);
    utterance.lang = "en-US";
    utterance.rate = ttsRate;
    utterance.pitch = 1;

    const chosenVoice = getChosenVoice();
    if (chosenVoice) {
      utterance.voice = chosenVoice;
      utterance.lang = chosenVoice.lang;
    }

    window.speechSynthesis.speak(utterance);
  };

  const startPracticeWordRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      practiceWordStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      practiceWordRecorderRef.current = mediaRecorder;
      const chunks: Blob[] = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setPracticeWordAudioUrl(url);
      };

      mediaRecorder.start();
      setPracticeWordIsRecording(true);
      setPracticeWordRecTime(0);
      setPracticeWordAudioUrl(null);
      setPracticeWordScore(null);
      setPracticeWordPhonemes(null);

      if (practiceWordTimerRef.current) clearInterval(practiceWordTimerRef.current);
      practiceWordTimerRef.current = setInterval(() => {
        setPracticeWordRecTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Failed to start word practice recording:", err);
      Swal.fire("Lỗi", "Không thể truy cập microphone để ghi âm từ này.", "error");
    }
  };

  const stopPracticeWordRecording = () => {
    if (practiceWordTimerRef.current) {
      clearInterval(practiceWordTimerRef.current);
      practiceWordTimerRef.current = null;
    }
    if (practiceWordRecorderRef.current && practiceWordRecorderRef.current.state !== "inactive") {
      practiceWordRecorderRef.current.stop();
    }
    if (practiceWordStreamRef.current) {
      practiceWordStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    setPracticeWordIsRecording(false);
  };

  const handleAnalyzePracticeWord = async () => {
    if (!practiceWordAudioUrl || !practiceWordObj) return;
    setPracticeWordIsAnalyzing(true);
    try {
      const blob = await (await fetch(practiceWordAudioUrl)).blob();
      const file = new File([blob], `practice-single-word.webm`, { type: "audio/webm" });
      const cleanWord = (practiceWordObj.word || "").replace(/[.,;:!?]$/, "").trim();

      const res = await pronunciationAnalyze({
        sentence: cleanWord,
        audioFile: file,
      });

      if (res) {
        let score = res.accuracyScore || res.overallScore || 0;
        let phonemes = null;
        if (res.words && res.words.length > 0) {
          score = res.words[0].score || score;
          phonemes = res.words[0].phonemes || null;
        }
        setPracticeWordScore(score);
        setPracticeWordPhonemes(phonemes);
      }
    } catch (error: any) {
      console.error("Word analysis error:", error);
      if (error?.response?.status === 402) return;
      const msg = error?.response?.data?.message || error?.message || "Đánh giá phát âm thử lại từ thất bại.";
      Swal.fire("Lỗi", msg, "error");
    } finally {
      setPracticeWordIsAnalyzing(false);
    }
  };

  const handleApplyPracticeWordScore = () => {
    if (!sentResult || !practiceWordObj || practiceWordScore === null) return;

    const updatedWords = sentResult.words.map((w: any) => {
      if (w.word === practiceWordObj.word) {
        return {
          ...w,
          score: practiceWordScore,
          phonemes: practiceWordPhonemes || w.phonemes,
        };
      }
      return w;
    });

    const newAccuracy = Math.round(
      updatedWords.reduce((sum: number, w: any) => sum + (w.score || 0), 0) / updatedWords.length
    );

    setSentResult({
      ...sentResult,
      words: updatedWords,
      accuracyScore: newAccuracy,
      overallScore: newAccuracy,
    });

    setPracticeWordObj(null);
    Swal.fire({
      title: "Thành công",
      text: `Đã cập nhật điểm phát âm mới cho từ "${practiceWordObj.word}" vào kết quả câu!`,
      icon: "success",
      confirmButtonColor: "#A855F7",
    });
  };

  const stopPlaybackTimer = () => {
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (autoLoad && text && text.trim()) {
      handleGetGuide(text);
    } else {
      setSentenceGuide(null);
    }
    return () => {
      stopPlaybackTimer();
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [text, autoLoad]);

  const handleGetGuide = async (textToAnalyze: string) => {
    const trimmed = textToAnalyze.trim();
    if (!trimmed) return;
    setLoadingGuide(true);
    setSentenceGuide(null);

    const originalTokens = trimmed.split(/\s+/).filter((w) => /[a-zA-Z0-9]/.test(w));
    const rawWords = originalTokens.map((w) => w.replace(/[^\w']/g, "").toLowerCase());
    const uniqueWords = Array.from(new Set(rawWords.filter((w) => w.length > 0)));

    if (uniqueWords.length === 0) {
      setLoadingGuide(false);
      return;
    }

    try {
      const result = (await fetchDictionary({ keyword: uniqueWords.join(",") })) as any;
      const dictItems = result?.items || [];
      const dictMap = new Map<string, any>();
      dictItems.forEach((item: any) => {
        if (item.word) {
          dictMap.set(item.word.toLowerCase(), item);
        }
      });

      const FUNCTION_WORDS = new Set([
        "the",
        "a",
        "an",
        "in",
        "on",
        "at",
        "to",
        "for",
        "of",
        "with",
        "by",
        "from",
        "about",
        "into",
        "through",
        "over",
        "under",
        "and",
        "but",
        "or",
        "so",
        "yet",
        "nor",
        "is",
        "are",
        "am",
        "was",
        "were",
        "be",
        "been",
        "being",
        "have",
        "has",
        "had",
        "do",
        "does",
        "did",
        "can",
        "could",
        "will",
        "would",
        "shall",
        "should",
        "may",
        "might",
        "must",
        "i",
        "you",
        "he",
        "she",
        "it",
        "we",
        "they",
        "me",
        "him",
        "her",
        "us",
        "them",
        "my",
        "your",
        "his",
        "its",
        "our",
        "their",
        "this",
        "that",
        "these",
        "those",
        "not",
        "no",
      ]);

      const isQuestion = trimmed.endsWith("?");
      const firstCleanWord = rawWords[0] || "";
      const isWhQuestion = ["who", "what", "where", "why", "how", "when", "which", "whose", "whom"].includes(
        firstCleanWord
      );
      const lastWordIntonation = isQuestion && !isWhQuestion ? "RISE" : "FALL";

      const boundaryAfterIndex = new Set<number>();
      originalTokens.forEach((token, i) => {
        if (/[,;:]$/.test(token)) boundaryAfterIndex.add(i);
      });

      const senseGroups: number[][] = [];
      let currentGroup: number[] = [];
      originalTokens.forEach((_token, i) => {
        currentGroup.push(i);
        if (boundaryAfterIndex.has(i)) {
          senseGroups.push(currentGroup);
          currentGroup = [];
        }
      });
      if (currentGroup.length > 0) senseGroups.push(currentGroup);

      const intonationMap = new Map<number, string>();
      senseGroups.forEach((group, groupIdx) => {
        const isLastGroup = groupIdx === senseGroups.length - 1;
        let lastContentIdx = -1;
        for (let k = group.length - 1; k >= 0; k--) {
          const tokenIdx = group[k];
          const w = rawWords[tokenIdx] || "";
          if (!FUNCTION_WORDS.has(w)) {
            lastContentIdx = tokenIdx;
            break;
          }
        }

        group.forEach((tokenIdx) => {
          const isLastTokenOverall = tokenIdx === originalTokens.length - 1;
          const w = rawWords[tokenIdx] || "";
          if (isLastTokenOverall) {
            intonationMap.set(tokenIdx, lastWordIntonation);
          } else if (FUNCTION_WORDS.has(w)) {
            intonationMap.set(tokenIdx, "REDUCED");
          } else if (tokenIdx === lastContentIdx) {
            intonationMap.set(tokenIdx, isLastGroup ? "FLAT" : "RISE");
          } else {
            intonationMap.set(tokenIdx, "FLAT");
          }
        });
      });

      const guidedWords = originalTokens.map((originalToken, index) => {
        const cleanWord = rawWords[index] || "";
        const dictEntry = dictMap.get(cleanWord);
        const ipaUs = dictEntry?.phonetic?.us || "";
        const ipaUk = dictEntry?.phonetic?.uk || "";
        const intonation = intonationMap.get(index) || "FLAT";
        const displayWord = originalToken.replace(/[.,;:!?]$/, "");
        const hasPauseAfter = boundaryAfterIndex.has(index);

        const cleanIpa = ipaUs.replace(/[[\]/()]/g, "").trim();
        let syllables: { text: string; stress: number }[] = [];

        if (cleanIpa) {
          if (cleanIpa.includes(".") || cleanIpa.includes("-")) {
            const parts = cleanIpa.replace(/-/g, ".").split(".");
            syllables = parts
              .map((part: string) => {
                let stress = 0;
                if (part.includes("ˈ")) stress = 1;
                else if (part.includes("ˌ")) stress = 2;
                const text = part.replace(/[ˈˌ]/g, "").trim();
                return text ? { text, stress } : null;
              })
              .filter(Boolean) as { text: string; stress: number }[];
          } else {
            let prep = cleanIpa.replace(/([ˈˌ])/g, ".$1");
            if (prep.startsWith(".")) prep = prep.substring(1);

            const IPA_VOWELS = [
              "aɪ",
              "eɪ",
              "ɔɪ",
              "aʊ",
              "oʊ",
              "ɪə",
              "eə",
              "ʊə",
              "aː",
              "iː",
              "uː",
              "ɔː",
              "ɑː",
              "ɜː",
              "æ",
              "ɑ",
              "ɒ",
              "ʌ",
              "ɔ",
              "ɛ",
              "ɪ",
              "i",
              "e",
              "o",
              "u",
              "ʊ",
              "ɜ",
              "ə",
            ];

            const tokens: { text: string; isVowel: boolean; isStress: boolean }[] = [];
            let remaining = prep;

            while (remaining.length > 0) {
              if (remaining.startsWith("ˈ") || remaining.startsWith("ˌ")) {
                tokens.push({ text: remaining[0], isVowel: false, isStress: true });
                remaining = remaining.substring(1);
                continue;
              }
              if (remaining.startsWith(".")) {
                tokens.push({ text: ".", isVowel: false, isStress: false });
                remaining = remaining.substring(1);
                continue;
              }
              let foundVowel = false;
              for (const v of IPA_VOWELS) {
                if (remaining.startsWith(v)) {
                  tokens.push({ text: v, isVowel: true, isStress: false });
                  remaining = remaining.substring(v.length);
                  foundVowel = true;
                  break;
                }
              }
              if (foundVowel) continue;
              tokens.push({ text: remaining[0], isVowel: false, isStress: false });
              remaining = remaining.substring(1);
            }

            const vowelIndices: number[] = [];
            tokens.forEach((t, idx) => {
              if (t.isVowel) vowelIndices.push(idx);
            });

            if (vowelIndices.length <= 1) {
              let stress = 0;
              if (prep.includes("ˈ")) stress = 1;
              else if (prep.includes("ˌ")) stress = 2;
              const text = prep.replace(/[ˈˌ.]/g, "").trim();
              if (text) syllables = [{ text, stress }];
            } else {
              const splitAtIndices = new Set<number>();
              for (let k = 0; k < vowelIndices.length - 1; k++) {
                const v1 = vowelIndices[k];
                const v2 = vowelIndices[k + 1];
                let hasBoundary = false;
                for (let m = v1 + 1; m < v2; m++) {
                  if (tokens[m].isStress || tokens[m].text === ".") {
                    hasBoundary = true;
                    break;
                  }
                }
                if (hasBoundary) continue;
                const consonants: number[] = [];
                for (let m = v1 + 1; m < v2; m++) consonants.push(m);
                if (consonants.length === 0) splitAtIndices.add(v1 + 1);
                else if (consonants.length === 1) splitAtIndices.add(consonants[0]);
                else splitAtIndices.add(consonants[Math.floor(consonants.length / 2)]);
              }

              let currentSyllableTokens: string[] = [];
              let currentStress = 0;
              tokens.forEach((t, idx) => {
                if (t.text === ".") {
                  if (currentSyllableTokens.length > 0) {
                    syllables.push({ text: currentSyllableTokens.join(""), stress: currentStress });
                    currentSyllableTokens = [];
                    currentStress = 0;
                  }
                  return;
                }
                if (t.isStress) {
                  if (currentSyllableTokens.length > 0) {
                    syllables.push({ text: currentSyllableTokens.join(""), stress: currentStress });
                    currentSyllableTokens = [];
                  }
                  currentStress = t.text === "ˈ" ? 1 : 2;
                  return;
                }
                if (splitAtIndices.has(idx)) {
                  if (currentSyllableTokens.length > 0) {
                    syllables.push({ text: currentSyllableTokens.join(""), stress: currentStress });
                    currentSyllableTokens = [];
                    currentStress = 0;
                  }
                }
                currentSyllableTokens.push(t.text);
              });
              if (currentSyllableTokens.length > 0) {
                syllables.push({ text: currentSyllableTokens.join(""), stress: currentStress });
              }
            }
          }
        }

        const hasSentenceEnd = /[.!?]$/.test(originalToken);
        return {
          word: displayWord,
          originalToken,
          ipaUs,
          ipaUk,
          syllables,
          intonation,
          hasPauseAfter,
          hasSentenceEnd,
          linkingWithNext: null as ILinkingInfo | null,
        };
      });

      guidedWords.forEach((gw: any, idx: number) => {
        if (idx < guidedWords.length - 1 && !gw.hasPauseAfter && !gw.hasSentenceEnd) {
          const nextGw = guidedWords[idx + 1];
          gw.linkingWithNext = computeLinking(
            gw.word || "",
            nextGw.word || "",
            gw.ipaUs || gw.ipaUk || "",
            nextGw.ipaUs || nextGw.ipaUk || ""
          );
        } else {
          gw.linkingWithNext = null;
        }
      });

      setSentenceGuide({
        words: guidedWords,
        originalText: trimmed,
      });
    } catch (error) {
      console.error("Failed to generate pronunciation guide:", error);
    } finally {
      setLoadingGuide(false);
    }
  };

  const WordFrequencyWave = ({
    gw,
    isSpeaking,
    isSpoken,
    isDark,
  }: {
    gw: any;
    isSpeaking: boolean;
    isSpoken: boolean;
    isDark: boolean;
  }) => {
    const syllables = gw.syllables || [];
    const primaryCount = syllables.filter((s: any) => s.stress === 1).length;
    const isReduced = gw.intonation === "REDUCED";
    const wordLen = (gw.originalToken || gw.word || "").length;

    // Generate 5-7 simulated frequency bars representing audio amplitude & pitch profile of the word
    const baseHeights = isReduced
      ? [3, 5, 4, 6, 3]
      : primaryCount > 0
        ? [6, 14, 22, 16, 9, 5]
        : wordLen > 6
          ? [5, 10, 16, 14, 11, 6]
          : [4, 9, 14, 8, 5];

    const barColor = isSpeaking
      ? "#10B981"
      : isSpoken
        ? "#10B981"
        : isDark
          ? "rgba(255, 255, 255, 0.18)"
          : "rgba(0, 0, 0, 0.14)";

    const barShadow = isSpeaking
      ? "0 0 12px rgba(16, 185, 129, 0.9)"
      : isSpoken
        ? "0 0 6px rgba(16, 185, 129, 0.5)"
        : "none";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "2.5px",
          height: 24,
          width: "100%",
          mt: 0.6,
          px: 0.5,
        }}
      >
        {baseHeights.map((h, i) => {
          const transDelay = `${(i * 0.05).toFixed(2)}s`;
          return (
            <Box
              key={i}
              sx={{
                width: "2.5px",
                height: `${h}px`,
                bgcolor: barColor,
                borderRadius: "2px",
                boxShadow: barShadow,
                transform: isSpeaking ? "scaleY(1.2)" : "scaleY(1)",
                transition:
                  "background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                transitionDelay: transDelay,
              }}
            />
          );
        })}
      </Box>
    );
  };

  const EqualizerWave = ({ activeColor = "#6366F1", height = 16 }: { activeColor?: string; height?: number }) => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        height: `${height}px`,
        px: 0.5,
      }}
    >
      {[
        { delay: "0s", duration: "0.45s", min: "3px", max: `${height}px` },
        { delay: "0.15s", duration: "0.55s", min: "5px", max: `${Math.round(height * 0.85)}px` },
        { delay: "0.3s", duration: "0.4s", min: "3px", max: `${Math.round(height * 0.7)}px` },
        { delay: "0.1s", duration: "0.6s", min: "4px", max: `${height}px` },
      ].map((bar, i) => (
        <Box
          key={i}
          sx={{
            width: "2.5px",
            bgcolor: activeColor,
            borderRadius: "2px",
            animation: `eqPulse ${bar.duration} infinite ease-in-out alternate`,
            animationDelay: bar.delay,
            "@keyframes eqPulse": {
              "0%": { height: bar.min, opacity: 0.4 },
              "100%": { height: bar.max, opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  );

  const handlePlaySentence = (sentWords: Array<{ gw: any; globalIdx: number }>, sIdx: number) => {
    if (!sentWords || sentWords.length === 0 || typeof window === "undefined" || !window.speechSynthesis) return;
    stopPlaybackTimer();
    window.speechSynthesis.cancel();

    if (playingSentenceIdx === sIdx) {
      setPlayingSentenceIdx(null);
      setPlayingWordGlobalIdx(null);
      return;
    }

    setSpokenWordIndices(new Set());
    const wordBounds: Array<{ globalIdx: number; start: number; end: number }> = [];
    let cumulativeChars = 0;
    let cumulativeMs = 0;

    const wordTimeline = sentWords.map(({ gw, globalIdx }) => {
      const wordStr = (gw.originalToken || gw.word || "").trim();
      const startChar = cumulativeChars;
      const endChar = startChar + wordStr.length;
      cumulativeChars += wordStr.length + 1;
      wordBounds.push({ globalIdx, start: startChar, end: endChar });

      const sylCount = gw.syllables?.length || Math.max(1, Math.ceil(wordStr.length / 3.2));
      const durationMs = Math.max(160, (sylCount * 200 + (gw.hasPauseAfter ? 140 : 0)) / ttsRate);
      const startMs = cumulativeMs;
      cumulativeMs += durationMs;
      return { globalIdx, startMs, endMs: cumulativeMs };
    });

    const sentenceText = sentWords
      .map(({ gw }) => (gw.originalToken || gw.word || "").trim())
      .join(" ")
      .trim();
    if (!sentenceText) return;

    const utterance = new SpeechSynthesisUtterance(sentenceText);
    utterance.lang = "en-US";
    utterance.rate = ttsRate;
    utterance.pitch = 1;

    const chosenVoice = getChosenVoice();
    if (chosenVoice) {
      utterance.voice = chosenVoice;
      utterance.lang = chosenVoice.lang;
    }

    setPlayingSentenceIdx(sIdx);
    const firstWordIdx = sentWords[0]?.globalIdx;
    if (firstWordIdx !== undefined) {
      setPlayingWordGlobalIdx(firstWordIdx);
      setSpokenWordIndices(new Set([firstWordIdx]));
    }

    const startTimeMs = Date.now();
    playbackTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeMs;
      const activeWord = wordTimeline.find((w) => elapsed >= w.startMs && elapsed < w.endMs);
      if (activeWord) {
        setPlayingWordGlobalIdx(activeWord.globalIdx);
        setSpokenWordIndices((prev) => {
          const next = new Set(prev);
          wordTimeline.forEach((w) => {
            if (w.startMs <= activeWord.startMs) next.add(w.globalIdx);
          });
          return next;
        });
      } else if (elapsed >= cumulativeMs) {
        stopPlaybackTimer();
        setPlayingSentenceIdx(null);
        setPlayingWordGlobalIdx(null);
        setSpokenWordIndices((prev) => {
          const next = new Set(prev);
          sentWords.forEach(({ globalIdx }) => next.add(globalIdx));
          return next;
        });
      }
    }, 25);

    utterance.onboundary = (event) => {
      if (event.name === "word" || !event.name) {
        const charIdx = event.charIndex;
        const matched = wordBounds.find((b) => charIdx >= b.start && charIdx <= b.end + 2);
        if (matched) {
          setPlayingWordGlobalIdx(matched.globalIdx);
          setSpokenWordIndices((prev) => {
            const next = new Set(prev);
            wordTimeline.forEach((w) => {
              if (w.globalIdx <= matched.globalIdx) next.add(w.globalIdx);
            });
            return next;
          });
        }
      }
    };

    utterance.onend = () => {
      stopPlaybackTimer();
      setPlayingSentenceIdx(null);
      setPlayingWordGlobalIdx(null);
      setSpokenWordIndices((prev) => {
        const next = new Set(prev);
        sentWords.forEach(({ globalIdx }) => next.add(globalIdx));
        return next;
      });
    };

    utterance.onerror = () => {
      stopPlaybackTimer();
      setPlayingSentenceIdx(null);
      setPlayingWordGlobalIdx(null);
    };

    window.speechSynthesis.speak(utterance);
  };

  const handleMouseUpSelection = () => {
    if (!onSelectTargetText) return;
    const sel = window.getSelection();
    const rawSelected = sel ? sel.toString().trim() : "";
    if (!rawSelected || rawSelected.length === 0) return;

    const cleaned = rawSelected.replace(/^[.,;:!?\s]+|[.,;:!?\s]+$/g, "");
    if (cleaned.length > 0 && text.toLowerCase().includes(cleaned.toLowerCase())) {
      onSelectTargetText(cleaned);
    }
  };

  const getHighlightedWordIndices = (): Set<number> => {
    if (!selectedTargetText || !sentenceGuide?.words) return new Set<number>();
    const targetClean = selectedTargetText.trim().toLowerCase();

    const allTokens = sentenceGuide.words.map((gw: any) =>
      (gw.originalToken || gw.word || "").replace(/[^\w']/g, "").toLowerCase()
    );
    const targetTokens = targetClean
      .split(/\s+/)
      .map((w) => w.replace(/[^\w']/g, "").toLowerCase())
      .filter(Boolean);

    if (targetTokens.length === 0) return new Set<number>();

    for (let i = 0; i <= allTokens.length - targetTokens.length; i++) {
      let isMatch = true;
      for (let j = 0; j < targetTokens.length; j++) {
        if (allTokens[i + j] !== targetTokens[j]) {
          isMatch = false;
          break;
        }
      }
      if (isMatch) {
        const set = new Set<number>();
        for (let k = 0; k < targetTokens.length; k++) {
          set.add(i + k);
        }
        return set;
      }
    }

    const targetSet = new Set(targetTokens);
    const fallbackSet = new Set<number>();
    allTokens.forEach((tok: string, idx: number) => {
      if (targetSet.has(tok)) fallbackSet.add(idx);
    });
    return fallbackSet;
  };

  const highlightedIndices = getHighlightedWordIndices();
  const isFullSentenceSelected =
    !selectedTargetText || selectedTargetText.trim().toLowerCase() === text.trim().toLowerCase();

  if (loadingGuide) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "24px",
          bgcolor: isDark ? "rgba(30, 41, 59, 0.4)" : "#fff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
          <CircularProgress size={36} sx={{ color: "#A855F7", mb: 2 }} />
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
            Đang tải hướng dẫn phát âm & ngữ điệu...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!sentenceGuide) return null;

  const INTONATION_ICONS: Record<string, { symbol: string; color: string; label: string }> = {
    RISE: { symbol: "↗", color: "#38BDF8", label: "Lên giọng" },
    FALL: { symbol: "↘", color: "#EF4444", label: "Xuống giọng" },
    FLAT: { symbol: "→", color: "#6366F1", label: "Giọng phẳng" },
    REDUCED: { symbol: "•", color: "#94A3B8", label: "Nhẹ/Lướt" },
  };

  const sentences: Array<Array<{ gw: any; globalIdx: number }>> = [];
  let currentSentence: Array<{ gw: any; globalIdx: number }> = [];

  sentenceGuide.words.forEach((gw: any, idx: number) => {
    currentSentence.push({ gw, globalIdx: idx });
    const cleanWord = (gw.word || "").trim();
    const origToken = (gw.originalToken || "").trim();
    const isSentenceEnd =
      gw.hasSentenceEnd ||
      /[.!?]$/.test(origToken) ||
      /[.!?]$/.test(cleanWord) ||
      cleanWord.endsWith(".") ||
      cleanWord.endsWith("?") ||
      cleanWord.endsWith("!");

    if (isSentenceEnd) {
      sentences.push(currentSentence);
      currentSentence = [];
    }
  });

  if (currentSentence.length > 0) {
    sentences.push(currentSentence);
  }

  return (
    <Paper
      elevation={0}
      onMouseUp={handleMouseUpSelection}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: "18px",
        bgcolor: isDark ? "rgba(30, 41, 59, 0.4)" : "#fff",
        border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(168, 85, 247, 0.15)"}`,
        boxShadow: isDark ? "0 6px 24px rgba(0,0,0,0.18)" : "0 6px 24px rgba(0,0,0,0.02)",
        userSelect: "text !important",
        WebkitUserSelect: "text !important",
      }}
    >
      {/* Boxed Guide & Legend Card */}
      <Paper
        elevation={0}
        sx={{
          p: 1.2,
          px: 1.6,
          mb: 1.8,
          borderRadius: "12px",
          bgcolor: isDark ? "rgba(168,85,247,0.06)" : "rgba(168,85,247,0.03)",
          border: `1px solid ${alpha("#A855F7", 0.2)}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.8,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              color: "#A855F7",
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              fontSize: "0.9rem",
            }}
          >
            <SchoolIcon sx={{ fontSize: "1.1rem" }} /> Hướng dẫn phát âm & ngữ điệu
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {availableVoices.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1.2,
                  py: 0.3,
                  borderRadius: "100px",
                  bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(168,85,247,0.06)",
                  border: `1px solid ${alpha("#A855F7", 0.25)}`,
                }}
              >
                <RecordVoiceOverIcon sx={{ color: "#A855F7", fontSize: "0.95rem", flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: isDark ? "#fff" : "#1e293b",
                    fontSize: "0.76rem",
                    whiteSpace: "nowrap",
                    display: { xs: "none", sm: "inline" },
                  }}
                >
                  Giọng đọc:
                </Typography>
                <Select
                  size="small"
                  value={selectedVoiceURI}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedVoiceURI(val);
                    localStorage.setItem("fong_selected_tts_voice", val);
                  }}
                  variant="standard"
                  disableUnderline
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: "#A855F7",
                    minWidth: 130,
                    maxWidth: { xs: 150, sm: 240 },
                    "& .MuiSelect-select": {
                      py: 0.2,
                      pr: "20px !important",
                      display: "flex",
                      alignItems: "center",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#A855F7",
                      fontSize: "1.1rem",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "14px",
                        maxHeight: 320,
                        bgcolor: isDark ? "#1e293b" : "#fff",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                        "& .MuiMenuItem-root": {
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          py: 0.8,
                          "&.Mui-selected": {
                            bgcolor: alpha("#A855F7", 0.15),
                            color: "#A855F7",
                            fontWeight: 800,
                          },
                        },
                      },
                    },
                  }}
                >
                  {availableVoices.map((voice) => {
                    const info = getVoiceDisplayInfo(voice);
                    return (
                      <MenuItem key={voice.voiceURI} value={voice.voiceURI}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          <span>{info.flag}</span>
                          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
                            {info.displayName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem" }}>
                            · {info.region}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>
            )}

            {onClose && (
              <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary", p: 0.4 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Legend row */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1.2, sm: 2 }, alignItems: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.82rem" }}>
            <strong style={{ color: "#10B981" }}>■ Ô Xanh lá</strong> = Trọng âm chính &nbsp;·&nbsp;
            <strong style={{ color: "#F59E0B" }}>■ Ô Vàng</strong> = Trọng âm phụ
          </Typography>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", sm: "block" }, my: 0.4, borderColor: alpha("#A855F7", 0.2) }}
          />

          {[
            { symbol: "↗", color: "#38BDF8", label: "Lên giọng (Rise)" },
            { symbol: "↘", color: "#EF4444", label: "Xuống giọng (Fall)" },
            { symbol: "→", color: "#6366F1", label: "Giọng phẳng (Flat)" },
            { symbol: "•", color: "#9CA3AF", label: "Nhẹ/Lướt (Reduced)" },
            { symbol: "|", color: "#A855F7", label: "Dừng ngắn (Pause)" },
          ].map((item) => (
            <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ color: item.color, fontWeight: 900, fontSize: "1rem", lineHeight: 1 }}>
                {item.symbol}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.78rem" }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Linking Sounds Legend Row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 1, sm: 1.8 },
            alignItems: "center",
            mt: 1,
            pt: 0.8,
            borderTop: `1px dashed ${alpha("#A855F7", 0.15)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#A855F7",
              fontWeight: 800,
              fontSize: "0.8rem",
              display: "flex",
              alignItems: "center",
              gap: 0.4,
            }}
          >
            🔗 Nối âm (Linking):
          </Typography>

          {[
            { badge: "C-V", color: "#10B981", label: "Phụ âm + Nguyên âm (hold‿on, have‿a)" },
            { badge: "+j", color: "#F59E0B", label: "Chèn /j/ (friendly‿and, see‿it)" },
            { badge: "+w", color: "#F97316", label: "Chèn /w/ (go‿out, you‿are)" },
            { badge: "tʃ/dʒ", color: "#EC4899", label: "Biến âm (meet‿you, did‿you)" },
            { badge: "C-C", color: "#8B5CF6", label: "Nối phụ âm (bad‿day)" },
          ].map((item) => (
            <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography
                sx={{
                  color: item.color,
                  fontWeight: 900,
                  fontSize: "0.74rem",
                  fontFamily: "monospace",
                  bgcolor: alpha(item.color, 0.12),
                  border: `1px solid ${alpha(item.color, 0.3)}`,
                  px: 0.5,
                  py: 0.05,
                  borderRadius: "4px",
                  lineHeight: 1.2,
                }}
              >
                ‿ {item.badge}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.76rem" }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Word guides list */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8, mb: 1.2 }}>
        {sentences.map((sentWords, sIdx) => {
          const sentenceText = sentWords
            .map(({ gw }) => (gw.originalToken || gw.word || "").trim())
            .join(" ")
            .trim();
          const isSelectedSentence =
            selectedTargetText && selectedTargetText.trim().toLowerCase() === sentenceText.toLowerCase();

          const isSelectionActiveInSentence =
            !isFullSentenceSelected &&
            highlightedIndices.size > 0 &&
            sentWords.some(({ globalIdx }) => highlightedIndices.has(globalIdx));

          const displayWords = isSelectionActiveInSentence
            ? sentWords.filter(({ globalIdx }) => highlightedIndices.has(globalIdx))
            : sentWords;

          return (
            <Box
              key={sIdx}
              sx={{
                p: 1.8,
                borderRadius: "16px",
                bgcolor: isSelectedSentence
                  ? alpha("#A855F7", 0.08)
                  : isDark
                    ? "rgba(0,0,0,0.2)"
                    : "rgba(99,102,241,0.03)",
                border: `1px solid ${
                  isSelectedSentence ? "#A855F7" : isDark ? "rgba(255,255,255,0.05)" : "rgba(99,102,241,0.1)"
                }`,
                transition: "all 0.2s ease",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.4,
                  pb: 1,
                  borderBottom: `1px dashed ${isDark ? "rgba(255,255,255,0.08)" : "rgba(99,102,241,0.12)"}`,
                  flexWrap: "wrap",
                  gap: 1.2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "#A855F7",
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                  }}
                >
                  <span>Câu {sIdx + 1}</span>
                  {sentences.length > 1 && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "text.secondary", fontWeight: 500, opacity: 0.8, fontSize: "0.78rem" }}
                    >
                      ({displayWords.length} từ)
                    </Typography>
                  )}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                  {/* TTS Speed Slider Control directly integrated beside Listen button */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      px: 1.3,
                      py: 0.25,
                      borderRadius: "100px",
                      bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(99,102,241,0.06)",
                      border: `1px solid ${alpha("#6366F1", 0.25)}`,
                    }}
                  >
                    <SpeedIcon sx={{ color: "#6366F1", fontSize: "1rem", flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: isDark ? "#fff" : "#1e293b",
                        fontSize: "0.76rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Tốc độ:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: "#6366F1",
                        fontSize: "0.76rem",
                        whiteSpace: "nowrap",
                        minWidth: 28,
                      }}
                    >
                      {ttsRate.toFixed(2).replace(/\.?0+$/, "")}x
                    </Typography>
                    <Slider
                      size="small"
                      value={ttsRate}
                      min={0.25}
                      max={1.25}
                      step={0.05}
                      onChange={(_e, val) => setTtsRate(val as number)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v}x`}
                      sx={{
                        color: "#6366F1",
                        width: 65,
                        py: 0.6,
                        "& .MuiSlider-thumb": {
                          width: 11,
                          height: 11,
                          "&:hover, &.Mui-focusVisible, &.Mui-active": {
                            boxShadow: "0 0 0 4px rgba(99,102,241,0.2)",
                          },
                        },
                        "& .MuiSlider-track": { height: 3 },
                        "& .MuiSlider-rail": { height: 3, opacity: 0.3 },
                      }}
                    />
                  </Box>

                  {/* Listen Button */}
                  <Button
                    size="small"
                    variant={playingSentenceIdx === sIdx ? "contained" : "outlined"}
                    onClick={() => handlePlaySentence(displayWords, sIdx)}
                    startIcon={
                      playingSentenceIdx === sIdx ? (
                        <EqualizerWave activeColor="#fff" height={14} />
                      ) : (
                        <Box component="span" sx={{ fontSize: "0.9rem", lineHeight: 1 }}>
                          🔊
                        </Box>
                      )
                    }
                    sx={{
                      borderRadius: "100px",
                      textTransform: "none",
                      fontWeight: 800,
                      fontSize: "0.76rem",
                      color: playingSentenceIdx === sIdx ? "#fff" : "#6366F1",
                      bgcolor:
                        playingSentenceIdx === sIdx
                          ? "#6366F1"
                          : isDark
                            ? "rgba(99,102,241,0.1)"
                            : "rgba(99,102,241,0.05)",
                      borderColor: alpha("#6366F1", 0.4),
                      py: 0.35,
                      px: 1.4,
                      boxShadow: playingSentenceIdx === sIdx ? "0 4px 12px rgba(99, 102, 241, 0.35)" : "none",
                      "&:hover": {
                        borderColor: "#6366F1",
                        bgcolor: playingSentenceIdx === sIdx ? "#4F46E5" : alpha("#6366F1", 0.15),
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {playingSentenceIdx === sIdx
                      ? "Đang đọc..."
                      : isSelectionActiveInSentence
                        ? "Nghe đoạn chọn"
                        : "Nghe toàn bộ"}
                  </Button>

                  <Button
                    size="small"
                    variant={shadowingSentenceIdx === sIdx ? "contained" : "outlined"}
                    onClick={() => {
                      if (shadowingSentenceIdx === sIdx) {
                        stopShadowingLoop();
                        setShadowingSentenceIdx(null);
                      } else {
                        stopSentenceRecording();
                        setActiveSentenceRecIdx(null);
                        handleOpenShadowingStudio(sentWords, sIdx, selectedTargetText || undefined);
                      }
                    }}
                    startIcon={<RecordVoiceOverIcon sx={{ fontSize: "14px !important" }} />}
                    sx={{
                      borderRadius: "100px",
                      textTransform: "none",
                      fontWeight: 800,
                      fontSize: "0.76rem",
                      py: 0.35,
                      px: 1.4,
                      bgcolor: shadowingSentenceIdx === sIdx ? "#10B981" : alpha("#10B981", 0.08),
                      color: shadowingSentenceIdx === sIdx ? "#fff" : "#10B981",
                      borderColor: alpha("#10B981", 0.4),
                      boxShadow: shadowingSentenceIdx === sIdx ? "0 4px 14px rgba(16, 185, 129, 0.35)" : "none",
                      "&:hover": {
                        bgcolor: shadowingSentenceIdx === sIdx ? "#059669" : alpha("#10B981", 0.18),
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {shadowingSentenceIdx === sIdx ? "Đóng Shadowing" : "🗣️ Luyện Shadowing"}
                  </Button>

                  <Button
                    size="small"
                    variant={activeSentenceRecIdx === sIdx ? "contained" : "outlined"}
                    onClick={() => {
                      if (activeSentenceRecIdx === sIdx) {
                        stopSentenceRecording();
                        setActiveSentenceRecIdx(null);
                      } else {
                        stopShadowingLoop();
                        setShadowingSentenceIdx(null);
                        stopSentenceRecording();
                        setActiveSentenceRecIdx(sIdx);
                        setSentAudioUrl(null);
                        setSentResult(null);
                      }
                    }}
                    startIcon={<MicIcon sx={{ fontSize: "14px !important" }} />}
                    sx={{
                      borderRadius: "100px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.74rem",
                      py: 0.25,
                      px: 1.2,
                      bgcolor: activeSentenceRecIdx === sIdx ? "#EF4444" : alpha("#EF4444", 0.06),
                      color: activeSentenceRecIdx === sIdx ? "#fff" : "#EF4444",
                      borderColor: alpha("#EF4444", 0.4),
                      boxShadow: activeSentenceRecIdx === sIdx ? "0 2px 8px rgba(239, 68, 68, 0.3)" : "none",
                      "&:hover": {
                        bgcolor: activeSentenceRecIdx === sIdx ? "#DC2626" : alpha("#EF4444", 0.15),
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {activeSentenceRecIdx === sIdx ? "Đóng ghi âm" : "🎤 Ghi âm & Test"}
                  </Button>
                </Box>
              </Box>

              {shadowingSentenceIdx !== sIdx && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "18px 14px",
                    alignItems: "flex-start",
                  }}
                >
                  {displayWords.map(({ gw, globalIdx }) => {
                    const hasSyllables = gw.syllables.length > 0;
                    const inton = INTONATION_ICONS[gw.intonation || "FLAT"];
                    const isReduced = gw.intonation === "REDUCED";

                    const isSpeakingThisWord =
                      playingWordGlobalIdx === globalIdx || shadowingActiveTargetWordIdx === globalIdx;
                    const isSpokenWord = spokenWordIndices.has(globalIdx);

                    return (
                      <Box
                        key={globalIdx}
                        sx={{
                          display: "inline-flex",
                          flexDirection: "column",
                          alignItems: "center",
                          position: "relative",
                          cursor: "default",
                          p: 0.5,
                          borderRadius: "8px",
                          bgcolor: "transparent",
                          border: "none",
                          boxShadow: "none",
                          transform: "none",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Box sx={{ height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography
                            sx={{
                              fontSize: isReduced ? "1.65rem" : "1.6rem",
                              fontWeight: 900,
                              color: inton.color,
                              lineHeight: 1,
                              textShadow: isReduced ? "none" : `0 0 10px ${inton.color}77`,
                              filter: isReduced ? "none" : "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                            }}
                            title={inton.label}
                          >
                            {inton.symbol}
                          </Typography>
                        </Box>

                        <Box sx={{ height: 26, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {hasSyllables ? (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              {gw.syllables.map((syl: any, sylIdx: number) => {
                                const isPrimary = syl.stress === 1;
                                const isSecondary = syl.stress === 2;
                                const color = isPrimary
                                  ? "#10B981"
                                  : isSecondary
                                    ? "#F59E0B"
                                    : isDark
                                      ? "rgba(255,255,255,0.6)"
                                      : "#475569";
                                return (
                                  <Fragment key={sylIdx}>
                                    <Typography
                                      sx={{
                                        fontSize: isPrimary ? "0.86rem" : "0.78rem",
                                        fontWeight: isPrimary ? 900 : isSecondary ? 700 : 500,
                                        color,
                                        bgcolor: isPrimary
                                          ? alpha("#10B981", 0.1)
                                          : isSecondary
                                            ? alpha("#F59E0B", 0.06)
                                            : "transparent",
                                        px: isPrimary ? 0.45 : 0.1,
                                        py: 0.08,
                                        borderRadius: "4px",
                                        border: isPrimary ? `1px solid ${alpha("#10B981", 0.3)}` : "none",
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {syl.text}
                                    </Typography>
                                    {sylIdx < gw.syllables.length - 1 && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mx: 0.1, fontSize: "0.65rem" }}
                                      >
                                        ·
                                      </Typography>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "rgba(255,255,255,0.7)" : "#475569",
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                fontFamily: "monospace",
                              }}
                            >
                              {gw.ipaUs ? gw.ipaUs.replace(/[[\]/]/g, "") : gw.ipa || "—"}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: "1rem",
                              color: isSpeakingThisWord ? "#10B981" : isDark ? "#fff" : "#1E2937",
                              lineHeight: 1.2,
                              px: 0.6,
                              py: 0.15,
                              borderRadius: "4px",
                            }}
                          >
                            {(gw.originalToken || gw.word || "").trim()}
                          </Typography>
                        </Box>

                        <WordFrequencyWave
                          gw={gw}
                          isSpeaking={isSpeakingThisWord}
                          isSpoken={isSpokenWord}
                          isDark={isDark}
                        />

                        {gw.hasPauseAfter && (
                          <Box
                            sx={{
                              position: "absolute",
                              right: -11,
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "1.05rem",
                              color: "#10B981",
                              fontWeight: 900,
                              lineHeight: 1,
                              opacity: 0.7,
                            }}
                            title="Dừng ngắn (Pause)"
                          >
                            |
                          </Box>
                        )}

                        {gw.linkingWithNext && !gw.hasPauseAfter && (
                          <Tooltip title={gw.linkingWithNext.description} arrow placement="top">
                            <Box
                              sx={{
                                position: "absolute",
                                right: -12,
                                bottom: 20,
                                transform: "translateX(50%)",
                                zIndex: 3,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.2,
                                cursor: "help",
                                bgcolor: isDark ? "rgba(15,23,42,0.95)" : "#ffffff",
                                border: `1px solid ${alpha(gw.linkingWithNext.color, 0.45)}`,
                                borderRadius: "6px",
                                px: 0.45,
                                py: 0.08,
                                boxShadow: `0 2px 6px ${alpha(gw.linkingWithNext.color, 0.25)}`,
                                "&:hover": {
                                  borderColor: gw.linkingWithNext.color,
                                  transform: "translateX(50%) scale(1.12)",
                                },
                                transition: "all 0.15s ease",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.82rem",
                                  fontWeight: 900,
                                  color: gw.linkingWithNext.color,
                                  lineHeight: 1,
                                }}
                              >
                                {gw.linkingWithNext.symbol}
                              </Typography>
                              {gw.linkingWithNext.badge && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.62rem",
                                    fontWeight: 800,
                                    color: gw.linkingWithNext.color,
                                    lineHeight: 1,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {gw.linkingWithNext.badge}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Auto-Looping Shadowing Player Studio Panel */}
              {shadowingSentenceIdx === sIdx && (
                <Box sx={{ mt: 1.5 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.2, sm: 1.6 },
                      borderRadius: "14px",
                      bgcolor: isDark ? "rgba(6, 78, 59, 0.25)" : "rgba(236, 253, 245, 0.7)",
                      border: `1.5px solid ${alpha("#10B981", 0.35)}`,
                      boxShadow: "0 6px 20px rgba(16, 185, 129, 0.1)",
                    }}
                  >
                    {/* Compact Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <RecordVoiceOverIcon sx={{ color: "#10B981", fontSize: "1.2rem" }} />
                        {shadowingIsLooping ? (
                          <Chip
                            label={`🔄 Đang lặp đọc (Lần ${shadowingCurrentLoop}${shadowingLoopLimit > 0 ? `/${shadowingLoopLimit}` : ""})`}
                            size="small"
                            sx={{
                              fontWeight: 800,
                              bgcolor: "#10B981",
                              color: "#fff",
                              fontSize: "0.74rem",
                              height: 24,
                            }}
                          />
                        ) : (
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800, color: "#10B981", fontSize: "0.88rem" }}
                          >
                            Luyện tập Shadowing
                          </Typography>
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() => {
                          stopShadowingLoop();
                          setShadowingSentenceIdx(null);
                        }}
                        sx={{ color: "text.secondary", p: 0.4 }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>

                    {/* Looping Controls Toolbar */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 0.9,
                        px: 1.4,
                        mb: 1.2,
                        borderRadius: "10px",
                        bgcolor: isDark ? "rgba(15, 23, 42, 0.6)" : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                        {/* Speed Slider Control */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.6,
                            px: 1.2,
                            py: 0.2,
                            borderRadius: "100px",
                            bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(168,85,247,0.06)",
                            border: `1px solid ${alpha("#A855F7", 0.25)}`,
                          }}
                        >
                          <SpeedIcon sx={{ color: "#A855F7", fontSize: "0.95rem", flexShrink: 0 }} />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 800,
                              color: isDark ? "#fff" : "#1e293b",
                              fontSize: "0.74rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Tốc độ:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 800,
                              color: "#A855F7",
                              fontSize: "0.74rem",
                              whiteSpace: "nowrap",
                              minWidth: 28,
                            }}
                          >
                            {shadowingSpeed.toFixed(2).replace(/\.?0+$/, "")}x
                          </Typography>
                          <Slider
                            size="small"
                            value={shadowingSpeed}
                            min={0.25}
                            max={1.25}
                            step={0.05}
                            onChange={(_e, val) => {
                              const newSpeed = val as number;
                              setShadowingSpeed(newSpeed);
                              if (shadowingIsLooping) {
                                startShadowingLoop(undefined, newSpeed);
                              }
                            }}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${v}x`}
                            sx={{
                              color: "#A855F7",
                              width: 60,
                              py: 0.5,
                              "& .MuiSlider-thumb": {
                                width: 10,
                                height: 10,
                                "&:hover, &.Mui-focusVisible, &.Mui-active": {
                                  boxShadow: "0 0 0 4px rgba(168,85,247,0.2)",
                                },
                              },
                              "& .MuiSlider-track": { height: 2.8 },
                              "& .MuiSlider-rail": { height: 2.8, opacity: 0.3 },
                            }}
                          />
                        </Box>

                        {/* Pause Interval Selector */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 800, color: "text.secondary", fontSize: "0.74rem" }}
                          >
                            Khoảng nghỉ:
                          </Typography>
                          <Select
                            size="small"
                            value={shadowingPauseInterval}
                            onChange={(e) => {
                              const newPause = Number(e.target.value);
                              setShadowingPauseInterval(newPause);
                              if (shadowingIsLooping) {
                                startShadowingLoop(undefined, undefined, newPause);
                              }
                            }}
                            sx={{
                              height: 26,
                              fontSize: "0.74rem",
                              fontWeight: 800,
                              borderRadius: "100px",
                              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                            }}
                          >
                            <MenuItem value={1.0} sx={{ fontSize: "0.74rem", fontWeight: 700 }}>
                              1.0s (Ngắn)
                            </MenuItem>
                            <MenuItem value={1.5} sx={{ fontSize: "0.74rem", fontWeight: 700 }}>
                              1.5s (Vừa)
                            </MenuItem>
                            <MenuItem value={2.5} sx={{ fontSize: "0.74rem", fontWeight: 700 }}>
                              2.5s (Dài)
                            </MenuItem>
                          </Select>
                        </Box>

                        {/* Loop Limit Selector */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 800, color: "text.secondary", fontSize: "0.74rem" }}
                          >
                            Số lần lặp:
                          </Typography>
                          <Select
                            size="small"
                            value={shadowingLoopLimit}
                            onChange={(e) => {
                              const newLimit = Number(e.target.value);
                              setShadowingLoopLimit(newLimit);
                              if (shadowingIsLooping) {
                                startShadowingLoop(undefined, undefined, undefined, newLimit);
                              }
                            }}
                            sx={{
                              height: 26,
                              fontSize: "0.74rem",
                              fontWeight: 800,
                              borderRadius: "100px",
                              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                            }}
                          >
                            <MenuItem value={0} sx={{ fontSize: "0.74rem", fontWeight: 700 }}>
                              ∞ Lặp liên tục
                            </MenuItem>
                            <MenuItem value={3} sx={{ fontSize: "0.74rem", fontWeight: 700 }}>
                              3 lần
                            </MenuItem>
                            <MenuItem value={5} sx={{ fontSize: "0.74rem", fontWeight: 700 }}>
                              5 lần
                            </MenuItem>
                            <MenuItem value={10} sx={{ fontSize: "0.74rem", fontWeight: 700 }}>
                              10 lần
                            </MenuItem>
                          </Select>
                        </Box>
                      </Box>
                    </Paper>

                    {/* 1. Mouse Drag Selection Text Box */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.1,
                        px: 1.4,
                        mb: 1.2,
                        borderRadius: "12px",
                        bgcolor: isDark ? "rgba(15, 23, 42, 0.4)" : "#fff",
                        border: `1.5px solid ${alpha("#10B981", 0.3)}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.7,
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 800,
                            color: "#10B981",
                            fontSize: "0.86rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          ✂️ Chọn và bôi đen từ hoặc cụm từ muốn luyện tập
                        </Typography>

                        <Button
                          size="small"
                          variant={
                            !shadowingTargetText || shadowingTargetText === sentenceText ? "contained" : "outlined"
                          }
                          onClick={() => {
                            setShadowingTargetText(sentenceText);
                            if (shadowingIsLooping) {
                              startShadowingLoop(sentenceText);
                            }
                          }}
                          sx={{
                            borderRadius: "100px",
                            fontSize: "0.72rem",
                            fontWeight: 800,
                            py: 0.15,
                            px: 1.2,
                            bgcolor:
                              !shadowingTargetText || shadowingTargetText === sentenceText ? "#10B981" : "transparent",
                            color: !shadowingTargetText || shadowingTargetText === sentenceText ? "#fff" : "#10B981",
                            borderColor: alpha("#10B981", 0.4),
                            textTransform: "none",
                          }}
                        >
                          🌐 Chọn lại toàn câu
                        </Button>
                      </Box>

                      {/* Selectable Sentence Text Box */}
                      <Box
                        onMouseUp={() => {
                          const selection = window.getSelection();
                          const selText = selection ? selection.toString().trim() : "";
                          if (selText && selText.length >= 1) {
                            setShadowingTargetText(selText);
                            if (shadowingIsLooping) {
                              startShadowingLoop(selText);
                            }
                          }
                        }}
                        sx={{
                          p: 1.1,
                          px: 1.4,
                          borderRadius: "10px",
                          bgcolor: isDark ? "rgba(0,0,0,0.3)" : "#f8fafc",
                          border: `1px dashed ${alpha("#10B981", 0.4)}`,
                          userSelect: "text",
                          cursor: "text",
                          transition: "all 0.2s ease",
                          "&::selection": {
                            bgcolor: "#10B981",
                            color: "#fff",
                          },
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.02rem",
                            lineHeight: 1.5,
                            color: isDark ? "#E2E8F0" : "#1E293B",
                            userSelect: "text",
                          }}
                        >
                          {sentenceText}
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Primary Action Button Bar: Right beneath highlight box so user sees action immediately */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 0.8,
                        px: 1.4,
                        mb: 1.2,
                        borderRadius: "10px",
                        bgcolor: isDark ? "rgba(16, 185, 129, 0.12)" : "rgba(16, 185, 129, 0.08)",
                        border: `1.5px solid ${alpha("#10B981", 0.4)}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            if (shadowingIsLooping) {
                              stopShadowingLoop();
                            } else {
                              startShadowingLoop();
                            }
                          }}
                          startIcon={
                            shadowingIsLooping ? (
                              <StopIcon sx={{ fontSize: 17 }} />
                            ) : (
                              <PlayArrowIcon sx={{ fontSize: 17 }} />
                            )
                          }
                          sx={{
                            borderRadius: "100px",
                            bgcolor: shadowingIsLooping ? "#EF4444" : "#10B981",
                            "&:hover": {
                              bgcolor: shadowingIsLooping ? "#DC2626" : "#059669",
                              transform: "scale(1.02)",
                            },
                            fontWeight: 800,
                            fontSize: "0.84rem",
                            px: 2.2,
                            py: 0.5,
                            textTransform: "none",
                            boxShadow: shadowingIsLooping
                              ? "0 3px 12px rgba(239, 68, 68, 0.3)"
                              : "0 3px 14px rgba(16, 185, 129, 0.35)",
                            transition: "all 0.2s ease",
                            ...(!shadowingIsLooping
                              ? {
                                  animation: "shadowPulse 2s infinite ease-in-out",
                                  "@keyframes shadowPulse": {
                                    "0%": {
                                      boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.7)",
                                    },
                                    "50%": {
                                      boxShadow: "0 0 14px 5px rgba(16, 185, 129, 0.4)",
                                    },
                                    "100%": {
                                      boxShadow: "0 0 0 0 rgba(16, 185, 129, 0)",
                                    },
                                  },
                                }
                              : {}),
                          }}
                        >
                          {shadowingIsLooping ? "⏹️ Tạm dừng Shadowing" : "▶️ Bắt đầu Shadowing"}
                        </Button>

                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            color: isDark ? "#A7F3D0" : "#047857",
                            fontSize: "0.8rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          🎯 Đang chọn:
                          <Box
                            component="span"
                            sx={{
                              bgcolor: isDark ? "rgba(16,185,129,0.3)" : "rgba(16,185,129,0.15)",
                              px: 1,
                              py: 0.2,
                              borderRadius: "5px",
                              color: "#10B981",
                              fontWeight: 800,
                            }}
                          >
                            "{shadowingTargetText || sentenceText}"
                          </Box>
                        </Typography>
                      </Box>

                      {shadowingTargetText && shadowingTargetText !== sentenceText && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setShadowingTargetText(sentenceText);
                            if (shadowingIsLooping) {
                              startShadowingLoop(sentenceText);
                            }
                          }}
                          sx={{
                            borderRadius: "100px",
                            fontSize: "0.72rem",
                            fontWeight: 800,
                            py: 0.15,
                            px: 1.1,
                            color: "#10B981",
                            borderColor: alpha("#10B981", 0.4),
                            textTransform: "none",
                            "&:hover": {
                              bgcolor: alpha("#10B981", 0.1),
                              borderColor: "#10B981",
                            },
                          }}
                        >
                          Khôi phục toàn câu
                        </Button>
                      )}
                    </Paper>

                    {/* 2. Reading Preview Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.3,
                        borderRadius: "12px",
                        bgcolor: isDark ? "rgba(15, 23, 42, 0.7)" : "#fff",
                        border: `1.5px solid ${shadowingIsLooping ? "#10B981" : alpha("#10B981", 0.3)}`,
                        boxShadow: shadowingIsLooping ? "0 6px 20px rgba(16, 185, 129, 0.16)" : "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 800,
                            color: "#10B981",
                            fontSize: "0.86rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          📖 Đoạn văn bản xác nhận để lặp đọc
                        </Typography>
                        {shadowingIsLooping && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                            <EqualizerWave activeColor="#10B981" height={15} />
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 800, color: "#10B981", fontSize: "0.76rem" }}
                            >
                              Đang phát lần {shadowingCurrentLoop}
                              {shadowingLoopLimit > 0 ? `/${shadowingLoopLimit}` : ""}... Hãy đọc nhại theo!
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Display words with EXACT RICH UI from main guide (Intonation Icon, Syllables/IPA, Word, Waveform) */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "14px 12px",
                          alignItems: "flex-start",
                          p: 1,
                          borderRadius: "10px",
                          bgcolor: isDark ? "rgba(0, 0, 0, 0.3)" : "#f8fafc",
                          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                        }}
                      >
                        {(() => {
                          const targetTokens = (shadowingTargetText || sentenceText)
                            .trim()
                            .split(/\s+/)
                            .map((t) => t.replace(/[^\w']/g, "").toLowerCase())
                            .filter((w) => w.length > 0);

                          let matchedGlobalIndices: number[] = [];

                          if (targetTokens.length > 0) {
                            const sentCleanWords = sentWords.map(({ gw }) =>
                              (gw.originalToken || gw.word || "").replace(/[^\w']/g, "").toLowerCase()
                            );

                            for (let start = 0; start <= sentCleanWords.length - targetTokens.length; start++) {
                              let match = true;
                              for (let k = 0; k < targetTokens.length; k++) {
                                if (sentCleanWords[start + k] !== targetTokens[k]) {
                                  match = false;
                                  break;
                                }
                              }
                              if (match) {
                                matchedGlobalIndices = sentWords
                                  .slice(start, start + targetTokens.length)
                                  .map(({ globalIdx }) => globalIdx);
                                break;
                              }
                            }

                            if (matchedGlobalIndices.length === 0) {
                              let tokenIdx = 0;
                              sentWords.forEach(({ gw, globalIdx }) => {
                                const cleanWord = (gw.originalToken || gw.word || "").replace(/[^\w']/g, "").toLowerCase();
                                if (tokenIdx < targetTokens.length && cleanWord === targetTokens[tokenIdx]) {
                                  matchedGlobalIndices.push(globalIdx);
                                  tokenIdx++;
                                }
                              });
                            }
                          }

                          const activeTargetGlobalIdx =
                            shadowingActiveTargetWordIdx >= 0 ? matchedGlobalIndices[shadowingActiveTargetWordIdx] : -1;

                          const wordsToRender =
                            shadowingTargetText &&
                            shadowingTargetText !== sentenceText &&
                            matchedGlobalIndices.length > 0
                              ? sentWords.filter(({ globalIdx }) => matchedGlobalIndices.includes(globalIdx))
                              : sentWords;

                          return wordsToRender.map(({ gw, globalIdx }, renderIdx) => {
                            const isLastInSnippet = renderIdx === wordsToRender.length - 1;
                            const hasSyllables = gw.syllables && gw.syllables.length > 0;
                            const inton = INTONATION_ICONS[gw.intonation || "FLAT"];
                            const isReduced = gw.intonation === "REDUCED";
                            const wordStr = (gw.originalToken || gw.word || "").trim();

                            const isSpeakingThisWord = shadowingIsLooping && activeTargetGlobalIdx === globalIdx;

                            return (
                              <Box
                                key={globalIdx}
                                sx={{
                                  display: "inline-flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  position: "relative",
                                  p: 0.5,
                                  borderRadius: "8px",
                                  opacity: 1,
                                  bgcolor: isSpeakingThisWord ? alpha("#10B981", 0.16) : "transparent",
                                  transition: "all 0.15s ease",
                                }}
                              >
                                {/* 1. High Definition Intonation Icon */}
                                <Box
                                  sx={{ height: 26, display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: isReduced ? "1.5rem" : "1.45rem",
                                      fontWeight: 900,
                                      color: isSpeakingThisWord ? "#10B981" : inton.color,
                                      lineHeight: 1,
                                      textShadow: isReduced
                                        ? "none"
                                        : `0 0 10px ${isSpeakingThisWord ? "#10B98177" : inton.color + "77"}`,
                                      filter: isReduced ? "none" : "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                                    }}
                                    title={inton.label}
                                  >
                                    {inton.symbol}
                                  </Typography>
                                </Box>

                                {/* 2. Syllables & Stress Breakdown or IPA */}
                                <Box
                                  sx={{ height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                  {hasSyllables ? (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                      {gw.syllables.map((syl: any, sylIdx: number) => {
                                        const isPrimary = syl.stress === 1;
                                        const isSecondary = syl.stress === 2;
                                        const color = isPrimary
                                          ? "#10B981"
                                          : isSecondary
                                            ? "#F59E0B"
                                            : isDark
                                              ? "rgba(255,255,255,0.6)"
                                              : "#475569";
                                        return (
                                          <Fragment key={sylIdx}>
                                            <Typography
                                              sx={{
                                                fontSize: isPrimary ? "0.82rem" : "0.74rem",
                                                fontWeight: isPrimary ? 900 : isSecondary ? 700 : 500,
                                                color,
                                                bgcolor: isPrimary
                                                  ? alpha("#10B981", 0.12)
                                                  : isSecondary
                                                    ? alpha("#F59E0B", 0.08)
                                                    : "transparent",
                                                px: isPrimary ? 0.4 : 0.1,
                                                py: 0.05,
                                                borderRadius: "4px",
                                                border: isPrimary ? `1px solid ${alpha("#10B981", 0.3)}` : "none",
                                                lineHeight: 1.3,
                                              }}
                                            >
                                              {syl.text}
                                            </Typography>
                                            {sylIdx < gw.syllables.length - 1 && (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ mx: 0.1, fontSize: "0.6rem" }}
                                              >
                                                ·
                                              </Typography>
                                            )}
                                          </Fragment>
                                        );
                                      })}
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: isDark ? "rgba(255,255,255,0.7)" : "#475569",
                                        fontSize: "0.76rem",
                                        fontWeight: 600,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {gw.ipaUs ? gw.ipaUs.replace(/[[\]/]/g, "") : gw.ipa || "—"}
                                    </Typography>
                                  )}
                                </Box>

                                {/* 3. Word Text */}
                                <Box
                                  sx={{ height: 22, display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 800,
                                      fontSize: "0.95rem",
                                      color: isSpeakingThisWord ? "#10B981" : isDark ? "#fff" : "#1E2937",
                                      lineHeight: 1.2,
                                      px: 0.5,
                                      py: 0.1,
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {wordStr}
                                  </Typography>
                                </Box>

                                {/* 4. Audio Frequency Wave Graph running under each word */}
                                <WordFrequencyWave
                                  gw={gw}
                                  isSpeaking={isSpeakingThisWord}
                                  isSpoken={false}
                                  isDark={isDark}
                                />

                                {gw.hasPauseAfter && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      right: -10,
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      fontSize: "1rem",
                                      color: "#10B981",
                                      fontWeight: 900,
                                      lineHeight: 1,
                                      opacity: 0.7,
                                    }}
                                    title="Dừng ngắn (Pause)"
                                  >
                                    |
                                  </Box>
                                )}

                                {gw.linkingWithNext && !gw.hasPauseAfter && !isLastInSnippet && (
                                  <Tooltip title={gw.linkingWithNext.description} arrow placement="top">
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        right: -11,
                                        bottom: 18,
                                        transform: "translateX(50%)",
                                        zIndex: 3,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.2,
                                        cursor: "help",
                                        bgcolor: isDark ? "rgba(15,23,42,0.95)" : "#ffffff",
                                        border: `1px solid ${alpha(gw.linkingWithNext.color, 0.45)}`,
                                        borderRadius: "5px",
                                        px: 0.4,
                                        py: 0.05,
                                        boxShadow: `0 2px 6px ${alpha(gw.linkingWithNext.color, 0.25)}`,
                                        "&:hover": {
                                          borderColor: gw.linkingWithNext.color,
                                          transform: "translateX(50%) scale(1.12)",
                                        },
                                        transition: "all 0.15s ease",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "0.78rem",
                                          fontWeight: 900,
                                          color: gw.linkingWithNext.color,
                                          lineHeight: 1,
                                        }}
                                      >
                                        {gw.linkingWithNext.symbol}
                                      </Typography>
                                      {gw.linkingWithNext.badge && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: "0.58rem",
                                            fontWeight: 800,
                                            color: gw.linkingWithNext.color,
                                            lineHeight: 1,
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {gw.linkingWithNext.badge}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          });
                        })()}
                      </Box>
                    </Paper>
                  </Paper>
                </Box>
              )}

              {/* Inline Sentence Level Recorder & Detailed Assessment Panel */}
              {activeSentenceRecIdx === sIdx && (
                <Box
                  sx={{
                    mt: 3,
                    pt: 2.5,
                    borderTop: `1px dashed ${isDark ? "rgba(255,255,255,0.12)" : "rgba(168,85,247,0.2)"}`,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      bgcolor: isDark ? "rgba(15, 23, 42, 0.7)" : "#f8fafc",
                      border: `1px solid ${alpha("#A855F7", 0.25)}`,
                      boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, color: "#A855F7", display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <MicIcon fontSize="small" /> Ghi âm & Chấm điểm riêng Câu {sIdx + 1}
                      </Typography>
                    </Box>

                    {/* Waveform Voice Recorder Component */}
                    <Box sx={{ mb: 3 }}>
                      <WaveformRecorder
                        isRecording={sentIsRecording}
                        recordingTime={sentRecordingTime}
                        audioUrl={sentAudioUrl}
                        isAnalyzing={sentIsAnalyzing}
                        onStartRecording={() => startSentenceRecording()}
                        onStopRecording={stopSentenceRecording}
                        onAnalyze={() => handleAnalyzeSentence(sentenceText)}
                        onReset={() => {
                          setSentAudioUrl(null);
                          setSentResult(null);
                        }}
                        maxRecordingTime={60}
                        subtitleText={
                          sentRecordingTime > 50
                            ? `Sắp hết thời gian! (${60 - sentRecordingTime}s)`
                            : `Sẵn sàng ghi âm câu ${sIdx + 1}`
                        }
                      />
                    </Box>

                    {/* Detailed Sentence Assessment Result */}
                    {sentResult && (
                      <Box
                        sx={{
                          mt: 3,
                          pt: 3,
                          borderTop: `1px dashed ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
                        }}
                      >
                        {/* 1. Overall Score Banner */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: "20px",
                            textAlign: "center",
                            background: "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
                            color: "#fff",
                            boxShadow: "0 8px 24px rgba(168, 85, 247, 0.25)",
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 0.5, fontWeight: 700 }}>
                            Điểm phát âm (Overall Score) - Câu {sIdx + 1}
                          </Typography>
                          <Typography variant="h2" sx={{ fontWeight: 900, my: 0.5 }}>
                            {Math.round(sentResult.overallScore || sentResult.accuracyScore || 0)}
                          </Typography>
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            {[
                              { label: "Chính xác (Accuracy)", value: sentResult.accuracyScore },
                              { label: "Lưu khoát (Fluency)", value: sentResult.fluencyScore },
                              { label: "Đầy đủ (Completeness)", value: sentResult.completenessScore },
                            ].map((stat) => (
                              <Grid item xs={4} key={stat.label}>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                  {Math.round(stat.value || 0)}%
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600 }}>
                                  {stat.label}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>

                        {/* 2. Model Sentence text colored by word score */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            mb: 3,
                            borderRadius: "16px",
                            bgcolor: isDark ? "rgba(30, 41, 59, 0.6)" : "#fff",
                            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(168,85,247,0.15)"}`,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, color: "text.primary" }}>
                            📝 Câu đã đọc & Kết quả phát âm từng từ:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "rgba(255,255,255,0.7)" : "#64748B",
                              mb: 1.5,
                              display: "block",
                              fontWeight: 600,
                            }}
                          >
                            👉{" "}
                            <Box component="span" sx={{ color: "#A855F7", fontWeight: 800 }}>
                              Nhấp vào từ ở dưới
                            </Box>{" "}
                            để xem phân tích chi tiết âm tiết (Phoneme Breakdown) của riêng từ đó hoặc test lại phát âm!
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "8px 12px",
                              p: 2,
                              bgcolor: isDark ? "rgba(0,0,0,0.3)" : "#f8fafc",
                              borderRadius: "14px",
                              mb: 2,
                            }}
                          >
                            {sentResult.words.map((wordObj: any, wIdx: number) => {
                              const score = wordObj.score || 0;
                              const color = getWordPronunciationColor(wordObj);
                              const isSelected = selectedDetailWordIdx === wIdx;

                              return (
                                <Tooltip
                                  key={wIdx}
                                  title={`Nhấp để xem chi tiết âm tiết & test lại từ "${wordObj.word}" (${Math.round(
                                    score
                                  )}đ)`}
                                  arrow
                                >
                                  <Paper
                                    onClick={() => setSelectedDetailWordIdx(wIdx)}
                                    elevation={0}
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 0.8,
                                      px: 1.4,
                                      py: 0.6,
                                      borderRadius: "10px",
                                      bgcolor: isSelected ? alpha(color, 0.22) : alpha(color, 0.1),
                                      border: `2px solid ${isSelected ? "#A855F7" : alpha(color, 0.35)}`,
                                      boxShadow: isSelected ? `0 4px 14px rgba(168, 85, 247, 0.35)` : "none",
                                      transform: isSelected ? "scale(1.05) translateY(-2px)" : "none",
                                      cursor: "pointer",
                                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                      "&:hover": {
                                        transform: "translateY(-2px) scale(1.06)",
                                        borderColor: "#A855F7",
                                      },
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "1.15rem",
                                        fontWeight: 800,
                                        color: color,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {wordObj.word}
                                    </Typography>
                                  </Paper>
                                </Tooltip>
                              );
                            })}
                          </Box>

                          {/* Legend */}
                          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                            {[
                              { label: "Tốt (>= 80đ)", color: "#10B981" },
                              { label: "Trung bình (60-79đ)", color: "#F59E0B" },
                              { label: "Cần cải thiện (< 60đ hoặc sai nguyên âm)", color: "#EF4444" },
                            ].map((legend) => (
                              <Box key={legend.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: legend.color }} />
                                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                  {legend.label}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Paper>

                        {/* 3. Detailed Phoneme Breakdown for the SINGLE selected word */}
                        {selectedDetailWordIdx !== null &&
                          sentResult.words[selectedDetailWordIdx] &&
                          (() => {
                            const wordObj = sentResult.words[selectedDetailWordIdx];
                            const score = wordObj.score || 0;
                            const color = getWordPronunciationColor(wordObj);
                            const isVowelErr = hasVowelPronunciationError(wordObj.phonemes);

                            return (
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 3,
                                  borderRadius: "18px",
                                  bgcolor: isDark ? "rgba(30, 41, 59, 0.6)" : "#fff",
                                  border: `1.5px solid ${alpha(color, 0.4)}`,
                                  boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                    flexWrap: "wrap",
                                    gap: 1.5,
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                                    <Typography variant="h6" sx={{ fontWeight: 900, color: color }}>
                                      {wordObj.word}
                                    </Typography>
                                    <Chip
                                      label={`${Math.round(score)} pts`}
                                      size="small"
                                      sx={{
                                        fontWeight: 800,
                                        bgcolor: alpha(color, 0.12),
                                        color: color,
                                        fontSize: "0.8rem",
                                      }}
                                    />
                                    {isVowelErr && (
                                      <Chip
                                        label="⚠️ Sai nguyên âm"
                                        size="small"
                                        sx={{
                                          fontWeight: 800,
                                          fontSize: "0.75rem",
                                          bgcolor: alpha("#EF4444", 0.15),
                                          color: "#EF4444",
                                          border: "1px solid rgba(239, 68, 68, 0.3)",
                                        }}
                                      />
                                    )}
                                  </Box>


                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleOpenPracticeWord(wordObj)}
                                    startIcon={<MicIcon sx={{ fontSize: "14px !important" }} />}
                                    sx={{
                                      borderRadius: "100px",
                                      fontSize: "0.75rem",
                                      fontWeight: 800,
                                      py: 0.4,
                                      px: 1.8,
                                      bgcolor: "#A855F7",
                                      textTransform: "none",
                                      "&:hover": { bgcolor: "#9333EA" },
                                    }}
                                  >
                                    🎤 Test phát âm lại riêng từ này
                                  </Button>
                                </Box>

                                <PhonemeBreakdown
                                  word={wordObj.word}
                                  phonemes={wordObj.phonemes || []}
                                  userAudioUrl={sentAudioUrl || undefined}
                                  extent={wordObj.extent}
                                />
                              </Paper>
                            );
                          })()}
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Single Word Practice Modal */}
      <Dialog
        open={Boolean(practiceWordObj)}
        onClose={() => !practiceWordIsAnalyzing && setPracticeWordObj(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            p: 1.5,
            bgcolor: isDark ? "#1e293b" : "#fff",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: "#A855F7", display: "flex", alignItems: "center", gap: 1 }}
          >
            🎯 Luyện phát âm từ: "{practiceWordObj?.word}"
          </Typography>
          <IconButton onClick={() => setPracticeWordObj(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderTop: "none" }}>
          {practiceWordObj && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2.5, py: 1 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    color:
                      (practiceWordObj.score || 0) >= 80
                        ? "#10B981"
                        : (practiceWordObj.score || 0) >= 60
                          ? "#F59E0B"
                          : "#EF4444",
                  }}
                >
                  {practiceWordObj.word}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: "text.secondary", mt: 0.5, fontWeight: 700 }}>
                  Điểm hiện tại: {Math.round(practiceWordObj.score || 0)}đ
                </Typography>
              </Box>

              <Button
                variant="outlined"
                onClick={() => handlePlaySingleWord(practiceWordObj.word)}
                startIcon={<Box component="span">🔊</Box>}
                sx={{
                  borderRadius: "100px",
                  textTransform: "none",
                  fontWeight: 800,
                  color: "#6366F1",
                  borderColor: alpha("#6366F1", 0.4),
                }}
              >
                Nghe giọng đọc mẫu (Native US)
              </Button>

              {/* Phoneme breakdown preview */}
              {practiceWordObj.phonemes && practiceWordObj.phonemes.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{ p: 1.5, width: "100%", borderRadius: "14px", bgcolor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc" }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: "text.secondary", mb: 1, display: "block" }}
                  >
                    Phiên âm & Âm tiết (IPA):
                  </Typography>
                  <PhonemeBreakdown
                    word={practiceWordObj.originalToken || practiceWordObj.word || ""}
                    phonemes={practiceWordObj.phonemes}
                    userAudioUrl={sentAudioUrl || undefined}
                    extent={practiceWordObj.extent}
                  />
                </Paper>
              )}

              {/* Waveform recorder for single word */}
              <Box sx={{ width: "100%" }}>
                <WaveformRecorder
                  isRecording={practiceWordIsRecording}
                  recordingTime={practiceWordRecTime}
                  audioUrl={practiceWordAudioUrl}
                  isAnalyzing={practiceWordIsAnalyzing}
                  onStartRecording={startPracticeWordRecording}
                  onStopRecording={stopPracticeWordRecording}
                  onAnalyze={handleAnalyzePracticeWord}
                  onReset={() => {
                    setPracticeWordAudioUrl(null);
                    setPracticeWordScore(null);
                    setPracticeWordPhonemes(null);
                  }}
                  maxRecordingTime={15}
                  subtitleText="Ghi âm phát âm lại riêng từ này"
                />
              </Box>

              {/* Practice Result feedback */}
              {practiceWordScore !== null && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    width: "100%",
                    borderRadius: "16px",
                    bgcolor: alpha("#10B981", 0.08),
                    border: "1px solid #10B981",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    Điểm phát âm mới của từ:
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      color: getWordPronunciationColor({
                        score: practiceWordScore,
                        phonemes: practiceWordPhonemes || [],
                      }),
                      my: 0.5,
                    }}
                  >
                    {practiceWordScore} pts
                  </Typography>
                  {practiceWordPhonemes && (
                    <PhonemeBreakdown
                      word={practiceWordObj.originalToken || practiceWordObj.word || ""}
                      phonemes={practiceWordPhonemes}
                      userAudioUrl={practiceWordAudioUrl || sentAudioUrl || undefined}
                    />
                  )}
                  <Button
                    variant="contained"
                    onClick={handleApplyPracticeWordScore}
                    sx={{
                      mt: 2,
                      borderRadius: "100px",
                      bgcolor: "#10B981",
                      "&:hover": { bgcolor: "#059669" },
                      fontWeight: 800,
                    }}
                  >
                    Cập nhật vào kết quả câu
                  </Button>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
